import type { SideView } from "@screener/protocol";

/**
 * Классы карточки плотности, общие для настоящей карточки экрана и
 * декоративной на странице входа: одни и те же строки в обоих местах, чтобы
 * вид не расходился. Литералами — сканер Tailwind находит классы в исходнике.
 */

/** Шапка карточки: одна линия 36 px без нижней черты (§88 п. 5). */
export const CARD_HEADER_CLASS = "flex h-9 items-center gap-[7px] pr-1.5 pl-2.5";

/** Колонки тела карточки: четыре дорожки строки — цена, время жизни, дистанция, объём. */
export const CARD_BODY_COLUMNS_CLASS =
  "grid grid-cols-[minmax(0,1fr)_minmax(52px,max-content)_minmax(44px,max-content)_minmax(44px,max-content)] gap-x-1.5 p-1";

/** Зазор между строками тела. У декоративной карточки заменён отступом строк. */
export const CARD_BODY_ROW_GAP_CLASS = "gap-y-0.5";

/** Тело карточки целиком: колонки и зазор строк. */
export const CARD_BODY_GRID_CLASS = `${CARD_BODY_COLUMNS_CLASS} ${CARD_BODY_ROW_GAP_CLASS}`;

/** Оболочка строки карточки без цветов и состояний: subgrid по колонкам тела. */
export const CARD_ROW_SHELL_CLASS =
  "relative col-span-full grid h-[22px] grid-cols-subgrid items-center overflow-hidden rounded-none px-1.5 text-left font-[450] text-[12.5px] leading-none text-foreground tabular-nums last:rounded-b-[6px]";

/** Подпись карточки без строк («Плотностей нет», «Скрыто фильтрами: N»). */
export const CARD_EMPTY_TEXT_CLASS = "col-span-full px-2.5 py-[9px] text-xs text-muted-foreground";

/**
 * Слагаемые высоты карточки экрана — те же величины, что в классах выше.
 * Раскладка считает высоту из них, а не меряет DOM
 * (DEN-104, design stage9-cards-layout-no-measure Д1): поменял класс — поменяй
 * число, иначе карточки налезут друг на друга. Сверка — e2e `cards-layout-height`.
 * Величины в rem умножаются на корневой шрифт, в px — нет.
 */
export const CARD_HEIGHT = {
  /** Шапка `h-9`. */
  headerRem: 2.25,
  /** Отступ тела `p-1` сверху и снизу — каждый. */
  bodyPaddingRem: 0.25,
  /** Строка `h-[22px]`. */
  rowPx: 22,
  /** Зазор между строками `gap-y-0.5`. */
  rowGapRem: 0.125,
  /** Подпись пустой карточки: высота строки `text-xs`. */
  emptyLineRem: 1,
  /** Подпись пустой карточки: отступ `py-[9px]` сверху и снизу — каждый. */
  emptyPaddingPx: 9,
} as const;

/**
 * Цвета стороны строки: полоса, заливка и черта у правого края. Токены
 * `--strip-*` выводятся из `--bid` и `--ask`; литералов цвета нет.
 */
export const CARD_STRIP_CLASSES: Record<SideView, { track: string; fill: string; edge: string }> = {
  ask: { track: "bg-strip-ask-track", fill: "bg-strip-ask-fill", edge: "border-ask" },
  bid: { track: "bg-strip-bid-track", fill: "bg-strip-bid-fill", edge: "border-bid" },
};
